import React, { useState, useEffect } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SiriGlow.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SmartSuggestions = ({ userId }) => {
  const navigate = useNavigate();
  const [actionCount, setActionCount] = useState(0);
  const [recentActions, setRecentActions] = useState({
    expenses: [],
    milestones: [],
    completedChecklists: []
  });
  const [suggestion, setSuggestion] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Listen for user actions via EventBus
  useEffect(() => {
    const handleAction = (event) => {
      const { type, data } = event.detail;
      
      setActionCount(prev => prev + 1);
      
      // Track recent actions
      setRecentActions(prev => {
        const updated = { ...prev };
        
        if (type === 'expense-added' || type === 'expense-updated') {
          updated.expenses = [data, ...prev.expenses].slice(0, 5);
        } else if (type === 'milestone-added' || type === 'milestone-updated') {
          updated.milestones = [data, ...prev.milestones].slice(0, 5);
        } else if (type === 'checklist-completed') {
          if (!prev.completedChecklists.includes(data)) {
            updated.completedChecklists = [...prev.completedChecklists, data];
          }
        }
        
        return updated;
      });
    };

    window.addEventListener('buildpilot-action', handleAction);
    return () => window.removeEventListener('buildpilot-action', handleAction);
  }, []);

  // Trigger AI analysis after 2-3 actions
  useEffect(() => {
    if (actionCount >= 2 && actionCount <= 3 && !isAnalyzing && !suggestion) {
      analyzePatternsAndSuggest();
    }
  }, [actionCount]);

  const analyzePatternsAndSuggest = async () => {
    setIsAnalyzing(true);
    
    try {
      const response = await axios.post(`${API}/suggest-checklist`, {
        recentExpenses: recentActions.expenses,
        recentMilestones: recentActions.milestones,
        completedChecklists: recentActions.completedChecklists
      });

      if (response.data.success && response.data.suggestion) {
        setSuggestion(response.data.suggestion);
        setIsVisible(true);
        
        // Reset action count for next cycle
        setActionCount(0);
      } else {
        // No suggestion, reset for next cycle
        setActionCount(0);
      }
    } catch (error) {
      console.error('Error getting suggestion:', error);
      setActionCount(0);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAddChecklist = () => {
    // Navigate to checklists with the suggested category
    const checklistSlug = suggestion.checklist.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-');
    navigate(`/tools/checklists?userId=${userId}&suggestion=${checklistSlug}`);
    handleDismiss();
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      setSuggestion(null);
      setActionCount(0);
    }, 300);
  };

  if (!suggestion || !isVisible) return null;

  return (
    <>
      {/* Semi-transparent backdrop */}
      <div 
        className={`fixed inset-0 bg-black/20 z-[9998] transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleDismiss}
      />

      {/* Siri-style notification pill */}
      <div 
        className={`fixed bottom-6 left-4 right-4 z-[9999] flex justify-center transition-transform duration-500 ease-out ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="siri-glow-container max-w-md w-full">
          <div className="siri-glow-border">
            <div className="smart-suggestions-gradient rounded-3xl p-5 shadow-2xl">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_smart-builder-14/artifacts/231u5dm5_C4A99C8E-2954-4650-8EFA-0F4C9217A9C7.png"
                    alt="BuildPilot Intelligence"
                    className="w-12 h-12 object-contain"
                  />
                  <div>
                    <h3 className="text-white font-semibold text-sm">BuildPilot Intelligence</h3>
                    <p className="text-white/80 text-xs">Smart Suggestion</p>
                  </div>
                </div>
                <button
                  onClick={handleDismiss}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="mb-4">
                <p className="text-white text-sm font-medium mb-2">
                  Try the "{suggestion.checklist}" checklist
                </p>
                <p className="text-white/90 text-xs leading-relaxed">
                  {suggestion.reason}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleAddChecklist}
                  className="flex-1 bg-white text-purple-600 text-sm font-semibold py-2.5 px-4 rounded-full hover:bg-white/90 transition-all flex items-center justify-center gap-2 shadow-lg"
                >
                  View Checklist
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2.5 bg-white/20 text-white text-sm font-medium rounded-full hover:bg-white/30 transition-colors backdrop-blur-sm"
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SmartSuggestions;
